import * as React from 'react';
import * as classNames from 'classnames';
import { withRouter, WithRouterProps } from 'react-router';

import { withPrefix } from '../i18n_helpers';
import FacetOption from './facet_option';
import ModalDialog from './modal_dialog';
import FormGroupWithInput from './form_group_with_input';
import FormGroupWithSelect from './form_group_with_select';
import { FilterOption } from './search_filters';
import FilterLink from './filter_link';
import { getItem, setItem } from '../local_storage_utils';
import { withUpdateURL, IUpdateURLProps } from './with_update_url';
import { useExpEnabled } from '../user_hooks';
import experiments from '../experiments';
import { MParticleEvent } from '../elog/mparticle_types';

interface IExternalProps {
  option: FilterOption;
  onClick?: Function;
  linkParams?: any;
  clickEvent?: MParticleEvent;
}

type IProps = IExternalProps & IUpdateURLProps & WithRouterProps;

const t = withPrefix('commons.proximityFilter');

export const PROXIMITY_PARAM = 'proximity';
const POSTAL_CODE_PARAM = 'postal_code';
const DISTANCE_PARAM = 'distance';
const DEFAULT_DISTANCE = '50';
const DISTANCES = [5, 25, 50, 100, 250, 500];

function buildDistanceValue(distance) {
  return {
    text: t('modalDistanceMiles', { distance }),
    value: distance,
  };
}

export function getProximityParams() {
  return {
    storedPostalCode: getItem(POSTAL_CODE_PARAM, ''),
    storedDistance: getItem(DISTANCE_PARAM, DEFAULT_DISTANCE),
  };
}

function setProximityParams(postalCode, distance) {
  setItem(POSTAL_CODE_PARAM, postalCode);
  setItem(DISTANCE_PARAM, distance);
}

function proximityParams(linkParams, postalCode, distance) {
  return {
    [POSTAL_CODE_PARAM]: postalCode,
    distance,
    ...linkParams,
  };
}

function handleClick(event, postalCode, openModal) {
  if (!postalCode) {
    event.preventDefault();
    openModal(true);
  }
}

function handleSubmit(postalCode, distance, openModal, location, updateUrl, router) {
  openModal(false);
  setProximityParams(postalCode, distance);
  const newProximityParams = proximityParams({ [PROXIMITY_PARAM]: true }, postalCode, distance);
  const queryParams = { ...location?.query, ...newProximityParams };
  const newLocation = updateUrl(location, queryParams);

  router.push({
    pathname: newLocation.pathname,
    query: queryParams,
  });
}

export function ProximityFacet(props: IProps) {
  const { storedPostalCode, storedDistance } = getProximityParams();
  const [modal, openModal] = React.useState(false);
  const [postalCode, setPostalCode] = React.useState(storedPostalCode);
  const [distance, setDistance] = React.useState(storedDistance);
  const { option, linkParams, clickEvent, location, updateUrl, router } = props;
  const linkClass = classNames({
    facet__link: true,
    'facet__link--checkbox': true,
    'facet__link--active': option.selected,
  });

  if (!useExpEnabled(experiments.PROXIMITY_FILTER_MARKETPLACE)) { return null; }

  return (
    <>
      <FacetOption
        paramName="proximity"
        active={option.selected}
      >
        <FilterLink
          className={linkClass}
          params={proximityParams(linkParams, postalCode, distance)}
          data-checked={option.selected || null}
          rel="nofollow"
          clickEvent={clickEvent}
          onClick={(e) => handleClick(e, postalCode, openModal)}
        >
          <div>
            {postalCode ? t('filterNameWithZip', { distance, postalCode }) : t('filterName', { distance })}
            <button
              type="button"
              className="button-as-link ml-space"
              onClick={(e) => {
                e.preventDefault();
                openModal(true);
              }}
            >
              <span className="size-80 weight-bold">
                {t('modalButton')}
              </span>
            </button>
          </div>
        </FilterLink>
      </FacetOption>
      <ModalDialog
        headerTitle="Adjust Location"
        isOpen={modal}
        onRequestClose={() => {
          openModal(false);
        }}
        onSubmit={() =>
          handleSubmit(
            postalCode,
            distance,
            openModal,
            location,
            updateUrl,
            router,
          )
        }
      >
        <div className="g-container">
          <div className="g-col-6">
            <FormGroupWithInput
              inputName={POSTAL_CODE_PARAM}
              label="Postal Code"
              updateField={(e) => { setPostalCode(e.postal_code); }}
              value={postalCode}
              numberOnly
              minLength={5}
              maxLength={5}
            />
          </div>
          <div className="g-col-6">
            <FormGroupWithSelect
              inputName={DISTANCE_PARAM}
              label="Radius"
              updateField={(e) => setDistance(e.distance)}
              value={distance}
              options={DISTANCES.map(buildDistanceValue)}
            />
          </div>
        </div>
      </ModalDialog>
    </>
  );
}

export default withRouter(withUpdateURL(ProximityFacet));



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/components/proximity_facet.tsx